// Business Invitation Service
// Handles customer requests for businesses not on platform
// Invites businesses to join based on customer demand

import SimpleStorage from '../utils/simpleStorage';
import { smsService } from './smsService';

export interface BusinessInvitation {
  id: string;
  businessName: string;
  businessType: string; // Category
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  zipcode: string;
  requestedBy: string[]; // Array of user IDs who want this business
  requestCount: number;
  status: 'pending' | 'invited' | 'joined' | 'declined';
  invitationSentDate?: string;
  invitationMethod?: 'sms' | 'email' | 'both';
  joinedDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserBusinessRequest {
  userId: string;
  userName: string;
  userEmail: string;
  businessName: string;
  businessType: string;
  location: string;
  notes?: string;
  createdAt: string;
}

class BusinessInvitationService {
  private storageKey = 'business_invitations';
  private requestsKey = 'user_business_requests';

  /**
   * User requests a business that's not on platform
   */
  async requestBusiness(request: {
    userId: string;
    userName: string;
    userEmail: string;
    businessName: string;
    businessType: string;
    businessPhone?: string;
    businessEmail?: string;
    location: string;
    zipcode: string;
    notes?: string;
  }): Promise<{ success: boolean; message: string; invitationId?: string }> {
    try {
      console.log('📝 User requesting business:', request.businessName);

      // Check if business already has an invitation
      const existingInvitation = await this.findInvitation(request.businessName, request.zipcode);

      if (existingInvitation) {
        // Add this user to the request list
        await this.addUserToRequest(existingInvitation.id, request.userId);
        
        // If enough users want it, auto-send invitation
        if (existingInvitation.requestCount >= 3 && existingInvitation.status === 'pending') {
          await this.sendInvitation(existingInvitation.id);
        }

        return {
          success: true,
          message: `We've noted your interest in ${request.businessName}. ${existingInvitation.requestCount + 1} customers have requested this business. We'll invite them soon!`,
          invitationId: existingInvitation.id,
        };
      }

      // Create new invitation request
      const invitation: BusinessInvitation = {
        id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        businessName: request.businessName,
        businessType: request.businessType,
        businessPhone: request.businessPhone,
        businessEmail: request.businessEmail,
        zipcode: request.zipcode,
        requestedBy: [request.userId],
        requestCount: 1,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await this.saveInvitation(invitation);

      // Save user request for tracking
      const userRequest: UserBusinessRequest = {
        userId: request.userId,
        userName: request.userName,
        userEmail: request.userEmail,
        businessName: request.businessName,
        businessType: request.businessType,
        location: request.location,
        notes: request.notes,
        createdAt: new Date().toISOString(),
      };
      await this.saveUserRequest(userRequest);

      return {
        success: true,
        message: `Thanks! We've recorded your request for ${request.businessName}. We'll reach out to them and notify you when they join!`,
        invitationId: invitation.id,
      };
    } catch (error) {
      console.error('Error requesting business:', error);
      return {
        success: false,
        message: 'Sorry, we couldn\'t process your request. Please try again.',
      };
    }
  }

  /**
   * Create an invitation from a business a sales rep found in person (e.g. a
   * scanned business card) and send it immediately — unlike requestBusiness,
   * this does not wait for the 3-request threshold since there's no customer
   * demand signal yet, just a rep's outreach.
   */
  async inviteBusinessDirectly(input: {
    businessName: string;
    businessType: string;
    businessAddress?: string;
    businessPhone?: string;
    businessEmail?: string;
    zipcode: string;
    invitedBy: string; // rep/admin user id, for attribution
  }): Promise<{ success: boolean; message: string; invitationId?: string; link?: string }> {
    if (!input.businessPhone && !input.businessEmail) {
      return {
        success: false,
        message: 'Add a phone number or email for this business before sending an invite.',
      };
    }

    try {
      const existingInvitation = await this.findInvitation(input.businessName, input.zipcode);
      const invitation: BusinessInvitation = existingInvitation ?? {
        id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        businessName: input.businessName,
        businessType: input.businessType,
        businessAddress: input.businessAddress,
        businessPhone: input.businessPhone,
        businessEmail: input.businessEmail,
        zipcode: input.zipcode,
        requestedBy: [input.invitedBy],
        requestCount: 1,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (existingInvitation) {
        invitation.businessAddress = invitation.businessAddress ?? input.businessAddress;
        invitation.businessPhone = invitation.businessPhone ?? input.businessPhone;
        invitation.businessEmail = invitation.businessEmail ?? input.businessEmail;
        await this.updateInvitation(invitation);
      } else {
        await this.saveInvitation(invitation);
      }

      const sent = await this.sendInvitation(invitation.id);
      if (!sent) {
        return {
          success: false,
          message: 'Saved the business, but the invite failed to send. Try resending from the invitations list.',
          invitationId: invitation.id,
        };
      }

      return {
        success: true,
        message: `Invite sent to ${input.businessName}.`,
        invitationId: invitation.id,
        link: this.generateInvitationLink(invitation.id),
      };
    } catch (error) {
      console.error('Error inviting business directly:', error);
      return { success: false, message: "Sorry, we couldn't send that invite. Please try again." };
    }
  }

  /**
   * Send invitation to business
   */
  async sendInvitation(invitationId: string): Promise<boolean> {
    try {
      const invitations = await this.getAllInvitations();
      const invitation = invitations.find(inv => inv.id === invitationId);

      if (!invitation) {
        console.error('Invitation not found:', invitationId);
        return false;
      }

      // Prepare invitation message
      const message = this.generateInvitationMessage(invitation);

      // Send via SMS if phone available
      if (invitation.businessPhone) {
        await smsService.sendSMS(invitation.businessPhone, message.sms);
      }

      // Send via email if email available (would need email service)
      if (invitation.businessEmail) {
        console.log('📧 Sending email to:', invitation.businessEmail);
        // TODO: Implement email service
      }

      // Update invitation status
      invitation.status = 'invited';
      invitation.invitationSentDate = new Date().toISOString();
      invitation.invitationMethod = invitation.businessPhone && invitation.businessEmail 
        ? 'both' 
        : invitation.businessPhone 
          ? 'sms' 
          : 'email';
      invitation.updatedAt = new Date().toISOString();

      await this.updateInvitation(invitation);

      // Notify requesting users
      await this.notifyRequestingUsers(invitation);

      console.log('✅ Invitation sent to:', invitation.businessName);
      return true;
    } catch (error) {
      console.error('Error sending invitation:', error);
      return false;
    }
  }

  /**
   * Generate invitation message
   */
  private generateInvitationMessage(invitation: BusinessInvitation): { sms: string; email: string } {
    // Import download configuration
    const { getSmartDownloadLink, getDownloadMessage } = require('../config/appDistributionConfig');
    const downloadLink = getSmartDownloadLink(invitation.id);
    
    const smsMessage = `Hi ${invitation.businessName}! 👋

${invitation.requestCount} customers on BookAPT want to book with you!

BookAPT is the fastest appointment booking app. Get:
✅ ${invitation.requestCount} ready-to-book customers
✅ AI-powered booking (30 seconds!)
✅ Payment processing built-in
✅ 30-day FREE trial

Join now: ${downloadLink}

Reply YES to get started or STOP to opt out.`;

    const emailMessage = `
<html>
  <body>
    <h2>🎉 Great News, ${invitation.businessName}!</h2>
    
    <p><strong>${invitation.requestCount} customers</strong> on BookAPT are looking to book with you right now!</p>
    
    <h3>Why Join BookAPT?</h3>
    <ul>
      <li>✅ <strong>${invitation.requestCount} Ready Customers</strong> - They're waiting to book</li>
      <li>✅ <strong>AI-Powered Booking</strong> - Customers book in 30 seconds via chat</li>
      <li>✅ <strong>Payment Processing</strong> - Get paid instantly, reduce no-shows</li>
      <li>✅ <strong>30-Day Free Trial</strong> - No credit card required</li>
      <li>✅ <strong>Easy Setup</strong> - Live in 5 minutes</li>
    </ul>
    
    <p><strong>Your customers are waiting!</strong></p>
    
    <a href="bookapt.io/join?ref=${invitation.id}" style="background: #007AFF; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; margin: 20px 0;">
      Join BookAPT Now
    </a>
    
    <p style="color: #666; font-size: 12px;">
      This invitation expires in 14 days. Don't miss out on ${invitation.requestCount} potential bookings!
    </p>
  </body>
</html>`;

    return { sms: smsMessage, email: emailMessage };
  }

  /**
   * Add user to existing invitation request
   */
  private async addUserToRequest(invitationId: string, userId: string): Promise<void> {
    const invitations = await this.getAllInvitations();
    const invitation = invitations.find(inv => inv.id === invitationId);

    if (invitation && !invitation.requestedBy.includes(userId)) {
      invitation.requestedBy.push(userId);
      invitation.requestCount = invitation.requestedBy.length;
      invitation.updatedAt = new Date().toISOString();
      await this.updateInvitation(invitation);
    }
  }

  /**
   * Find existing invitation for a business
   */
  private async findInvitation(businessName: string, zipcode: string): Promise<BusinessInvitation | null> {
    const invitations = await this.getAllInvitations();
    return invitations.find(
      inv => 
        inv.businessName.toLowerCase() === businessName.toLowerCase() &&
        inv.zipcode === zipcode &&
        inv.status !== 'declined'
    ) || null;
  }

  /**
   * Get all invitations
   */
  async getAllInvitations(): Promise<BusinessInvitation[]> {
    try {
      const data = await SimpleStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error getting invitations:', error);
      return [];
    }
  }

  /**
   * Get invitations by status
   */
  async getInvitationsByStatus(status: BusinessInvitation['status']): Promise<BusinessInvitation[]> {
    const allInvitations = await this.getAllInvitations();
    return allInvitations.filter(inv => inv.status === status);
  }

  /**
   * Get top requested businesses (for admin dashboard)
   */
  async getTopRequestedBusinesses(limit: number = 10): Promise<BusinessInvitation[]> {
    const allInvitations = await this.getAllInvitations();
    return allInvitations
      .filter(inv => inv.status === 'pending' || inv.status === 'invited')
      .sort((a, b) => b.requestCount - a.requestCount)
      .slice(0, limit);
  }

  /**
   * Save invitation
   */
  private async saveInvitation(invitation: BusinessInvitation): Promise<void> {
    const invitations = await this.getAllInvitations();
    invitations.push(invitation);
    await SimpleStorage.setItem(this.storageKey, JSON.stringify(invitations));
  }

  /**
   * Update invitation
   */
  private async updateInvitation(updatedInvitation: BusinessInvitation): Promise<void> {
    const invitations = await this.getAllInvitations();
    const index = invitations.findIndex(inv => inv.id === updatedInvitation.id);
    
    if (index !== -1) {
      invitations[index] = updatedInvitation;
      await SimpleStorage.setItem(this.storageKey, JSON.stringify(invitations));
    }
  }

  /**
   * Save user request
   */
  private async saveUserRequest(request: UserBusinessRequest): Promise<void> {
    try {
      const requests = await this.getAllUserRequests();
      requests.push(request);
      await SimpleStorage.setItem(this.requestsKey, JSON.stringify(requests));
    } catch (error) {
      console.error('Error saving user request:', error);
    }
  }

  /**
   * Get all user requests
   */
  async getAllUserRequests(): Promise<UserBusinessRequest[]> {
    try {
      const data = await SimpleStorage.getItem(this.requestsKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error getting user requests:', error);
      return [];
    }
  }

  /**
   * Notify requesting users when business joins
   */
  private async notifyRequestingUsers(invitation: BusinessInvitation): Promise<void> {
    // In production, send push notifications or emails
    console.log('📬 Notifying users about invitation sent:', {
      business: invitation.businessName,
      users: invitation.requestCount,
    });
  }

  /**
   * Mark business as joined
   */
  async markBusinessJoined(invitationId: string, businessId: string): Promise<boolean> {
    try {
      const invitations = await this.getAllInvitations();
      const invitation = invitations.find(inv => inv.id === invitationId);

      if (invitation) {
        invitation.status = 'joined';
        invitation.joinedDate = new Date().toISOString();
        invitation.updatedAt = new Date().toISOString();
        await this.updateInvitation(invitation);

        // Notify all requesting users
        await this.notifyUsersBusinessJoined(invitation);

        console.log('✅ Business joined:', invitation.businessName);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error marking business joined:', error);
      return false;
    }
  }

  /**
   * Notify users when their requested business joins
   */
  private async notifyUsersBusinessJoined(invitation: BusinessInvitation): Promise<void> {
    console.log('🎉 Notifying users - business joined:', {
      business: invitation.businessName,
      userCount: invitation.requestedBy.length,
    });

    // In production:
    // - Send push notifications
    // - Send emails
    // - Show in-app notification
    // Message: "Great news! {businessName} is now on BookAPT. Book your appointment now!"
  }

  /**
   * Get statistics for admin/analytics
   */
  async getStatistics(): Promise<{
    totalRequests: number;
    pendingInvitations: number;
    sentInvitations: number;
    joinedBusinesses: number;
    topCategories: { category: string; count: number }[];
    conversionRate: number;
  }> {
    const invitations = await this.getAllInvitations();

    const pending = invitations.filter(inv => inv.status === 'pending').length;
    const sent = invitations.filter(inv => inv.status === 'invited').length;
    const joined = invitations.filter(inv => inv.status === 'joined').length;

    // Calculate top requested categories
    const categoryCounts: { [key: string]: number } = {};
    invitations.forEach(inv => {
      categoryCounts[inv.businessType] = (categoryCounts[inv.businessType] || 0) + inv.requestCount;
    });

    const topCategories = Object.entries(categoryCounts)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const conversionRate = sent > 0 ? (joined / sent) * 100 : 0;

    return {
      totalRequests: invitations.length,
      pendingInvitations: pending,
      sentInvitations: sent,
      joinedBusinesses: joined,
      topCategories,
      conversionRate,
    };
  }

  /**
   * Auto-send invitations when threshold reached
   */
  async processAutomaticInvitations(): Promise<number> {
    const invitations = await this.getAllInvitations();
    let sentCount = 0;

    for (const invitation of invitations) {
      // Auto-send if 3+ users request and we have contact info
      if (
        invitation.status === 'pending' &&
        invitation.requestCount >= 3 &&
        (invitation.businessPhone || invitation.businessEmail)
      ) {
        const success = await this.sendInvitation(invitation.id);
        if (success) sentCount++;
      }
    }

    console.log(`✅ Auto-sent ${sentCount} invitations`);
    return sentCount;
  }

  /**
   * Generate invitation link with tracking
   */
  generateInvitationLink(invitationId: string): string {
    return `https://bookapt.io/join?ref=${invitationId}&source=customer_request`;
  }

  /**
   * Track invitation clicks
   */
  async trackInvitationClick(invitationId: string): Promise<void> {
    // In production, track clicks to measure interest
    console.log('👆 Invitation link clicked:', invitationId);
  }
}

export const businessInvitationService = new BusinessInvitationService();
export default businessInvitationService;

